// Polish Phase 1 (AUDIO-03 / A11Y-AUDIO-01) — Settings → Sound controls.
//
// The mute toggle is the WCAG 1.4.2 mechanism (a way to turn sound off);
// volume is a three-step refinement on the shared <Seg> radiogroup (no slider
// primitive exists yet). Both write the persistent $sfx* atoms; the engine
// (bound in RootApp via useAudioRoot) ramps its SFX bus from $sfxGain. Toggling
// ON plays a short tick so the change is immediately audible — and doubles as a
// quick "is my sound working?" probe.

import { useStore } from '@nanostores/react'
import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio.js'
import { $sfxMuted, $sfxVolume, type SfxVolume } from '../../stores/audio.js'
import { Seg } from '../components/Seg.js'

type SfxOnOff = 'on' | 'off'

const fieldsetStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  width: '100%',
  border: 'none',
  margin: 0,
  padding: 0,
}

const legendStyle: React.CSSProperties = {
  fontFamily: 'Fredoka, sans-serif',
  fontWeight: 700,
  fontSize: 'var(--font-size-16)',
  color: 'var(--color-text-primary)',
  padding: 0,
  marginBottom: 'var(--space-1)',
}

export function SoundToggle(): JSX.Element {
  const muted = useStore($sfxMuted)
  const volume = useStore($sfxVolume)
  const intl = useIntl()

  const sfxLabel = intl.formatMessage({
    id: 'settings.sound.sfx.label',
    defaultMessage: 'Sound effects',
  })
  const volumeLabel = intl.formatMessage({
    id: 'settings.sound.volume.label',
    defaultMessage: 'Volume',
  })

  return (
    <>
      <fieldset
        data-testid="sfx-toggle"
        aria-label={sfxLabel}
        className="picker-row"
        style={fieldsetStyle}
      >
        <legend style={legendStyle}>
          <FormattedMessage
            id="settings.sound.sfx.label"
            defaultMessage="Sound effects"
          />
        </legend>
        <Seg<SfxOnOff>
          ariaLabel={sfxLabel}
          value={muted ? 'off' : 'on'}
          onChange={(next) => {
            const nowMuted = next === 'off'
            $sfxMuted.set(nowMuted)
            // Audible confirmation when enabling.
            if (!nowMuted) playSfx('ui-confirm')
          }}
          options={[
            {
              value: 'off',
              label: (
                <FormattedMessage
                  id="settings.sound.sfx.opt.off"
                  defaultMessage="Off"
                />
              ),
            },
            {
              value: 'on',
              label: (
                <FormattedMessage
                  id="settings.sound.sfx.opt.on"
                  defaultMessage="On"
                />
              ),
            },
          ]}
        />
      </fieldset>

      <fieldset
        data-testid="sfx-volume"
        aria-label={volumeLabel}
        className="picker-row"
        style={fieldsetStyle}
      >
        <legend style={legendStyle}>
          <FormattedMessage
            id="settings.sound.volume.label"
            defaultMessage="Volume"
          />
        </legend>
        <Seg<SfxVolume>
          ariaLabel={volumeLabel}
          value={volume}
          onChange={(next) => {
            $sfxVolume.set(next)
            if (!muted) playSfx('ui-toggle')
          }}
          options={[
            {
              value: 'quiet',
              label: (
                <FormattedMessage
                  id="settings.sound.volume.opt.quiet"
                  defaultMessage="Quiet"
                />
              ),
            },
            {
              value: 'normal',
              label: (
                <FormattedMessage
                  id="settings.sound.volume.opt.normal"
                  defaultMessage="Normal"
                />
              ),
            },
            {
              value: 'loud',
              label: (
                <FormattedMessage
                  id="settings.sound.volume.opt.loud"
                  defaultMessage="Loud"
                />
              ),
            },
          ]}
        />
      </fieldset>
    </>
  )
}
